import type { Employee, EmployeePermissions } from '../types/firestore';
import type { User } from '../types';

export type EmployeePermissionKey = keyof EmployeePermissions;

export const emptyEmployeePermissions = (): EmployeePermissions => ({
  canAccessStudents: false,
  canAccessTeachers: false,
  canAccessParents: false,
  canAccessPayments: false,
  canAccessReports: false,
  canAccessHR: false,
  canAccessSettings: false,
  canCreate: false,
  canRead: true,
  canUpdate: false,
  canDelete: false,
  canExportData: false,
  canManageUsers: false,
  canViewFinancials: false,
  canProcessPayments: false,
  canManageSchedule: false,
  canAccessAssignments: false,
});

export const getDefaultEmployeePermissions = (
  role: Employee['role'],
  department: Employee['department'],
  current?: Partial<EmployeePermissions>
): EmployeePermissions => {
  const isAdmin = role === 'admin';
  const isSupervisor = role === 'supervisor' || isAdmin;
  const isHR = role === 'hr_manager' || department === 'hr' || isAdmin;
  const isFinance = role === 'accountant' || department === 'finance' || isAdmin;
  const isTeaching = role === 'teacher' || department === 'teaching' || isAdmin;
  const canManage = isAdmin || isSupervisor || isHR;

  return {
    ...emptyEmployeePermissions(),
    ...current,
    canAccessStudents: isTeaching || department === 'customer_service',
    canAccessTeachers: isSupervisor || isHR,
    canAccessParents: role === 'customer_service' || isSupervisor || isAdmin,
    canAccessPayments: isFinance,
    canAccessReports: canManage || isFinance,
    canAccessHR: isHR,
    canAccessSettings: isAdmin,
    canCreate: canManage,
    canRead: true,
    canUpdate: canManage,
    canDelete: isAdmin,
    canExportData: canManage || isFinance,
    canManageUsers: isAdmin || isHR,
    canViewFinancials: isFinance,
    canProcessPayments: isFinance,
    canManageSchedule: isTeaching,
    canAccessAssignments: isTeaching,
  };
};

export const hasEmployeePermission = (
  user: Pick<User, 'role' | 'permissions'> | null | undefined,
  permission: EmployeePermissionKey
): boolean => {
  if (!user) return false;
  if (user.role === 'teacher' || user.role === 'admin') return true;
  return Boolean(user.permissions?.[permission]);
};
